import React, { useState, useEffect } from 'react';
import paymentService from '../../services/paymentService';
import ContractService from '../../services/contractService';
import { useToast } from '../common/Toast';
import { AlertCircle, X, CheckCircle, Loader2 } from 'lucide-react';

const PaymentFormModal = ({
  isOpen,
  onClose,
  editPayment,
  onSuccess,
  defaultContractId,
}) => {
  const { success, error: showToastError } = useToast();
  const [modalError, setModalError] = useState('');
  const [loading, setLoading] = useState(false);
  const [contracts, setContracts] = useState([]);

  const [formData, setFormData] = useState({
    contract: '',
    paymentType: 'Milestone',
    amount: '',
    currency: 'USD',
    dueDate: '',
    status: 'Pending',
    paymentMethod: '',
    reference: '',
    notes: '',
  });

  // Fetch contracts when modal opens
  useEffect(() => {
    const loadData = async () => {
      try {
        const response = await ContractService.getContracts({ limit: 100 });
        const contractsList = response.data?.contracts || response.data || [];
        setContracts(contractsList);
      } catch (error) {
        console.error('Failed to fetch contracts for modal', error);
      }
    };

    if (isOpen) {
      setModalError('');
      loadData();

      if (editPayment) {
        const formattedDate = editPayment.dueDate
          ? new Date(editPayment.dueDate).toISOString().split('T')[0]
          : '';
        setFormData({
          ...editPayment,
          dueDate: formattedDate,
          contract: editPayment.contract?._id || editPayment.contract,
        });
      } else {
        setFormData({
          contract: defaultContractId || '',
          paymentType: 'Milestone',
          amount: '',
          currency: 'USD',
          dueDate: '',
          status: 'Pending',
          paymentMethod: '',
          reference: '',
          notes: '',
        });
      }
    }
  }, [isOpen, editPayment, defaultContractId]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (modalError) setModalError(''); // Clear error on change
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setModalError('');

    try {
      const payload = {
        ...formData,
        amount: parseFloat(formData.amount),
      };

      if (editPayment) {
        // CRITICAL FIX: Use MongoDB _id for the update call, not the display paymentId
        const idToUpdate = editPayment._id || editPayment.id;
        await paymentService.updatePayment(idToUpdate, payload);
        success('Payment record updated successfully');
      } else {
        await paymentService.createPayment(payload);
        success('New payment record created successfully');
      }

      onSuccess();
      onClose();
    } catch (error) {
      const errorMessage =
        error.message ||
        'Failed to save payment record. Please check your data.';
      setModalError(errorMessage);
      showToastError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="absolute inset-0 bg-gray-900/60 backdrop-blur-sm flex justify-center items-start z-[11000] p-4">
      <div className="mt-6 bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col transform transition-all">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-100 flex justify-between items-center bg-gray-50/50">
          <div>
            <h2 className="text-xl font-bold text-gray-800">
              {editPayment ? 'Edit Payment Record' : 'Add New Payment'}
            </h2>
            <p className="text-xs text-gray-500 mt-1">
              {editPayment
                ? `Reference: ${editPayment.paymentId}`
                : 'Enter financial transaction details below'}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-200 rounded-full text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        <div className="overflow-y-auto p-6">
          {/* Error Message UI */}
          {modalError && (
            <div className="mb-6 p-4 bg-red-50 border border-red-100 rounded-lg flex items-start gap-3 text-red-700 animate-in fade-in slide-in-from-top-2">
              <AlertCircle size={20} className="shrink-0 mt-0.5" />
              <div className="text-sm font-medium">{modalError}</div>
            </div>
          )}

          <form id="payment-form" onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Contract Selection */}
              <div className="space-y-1.5">
                <label className="text-sm font-semibold text-gray-700">
                  Contract *
                </label>
                <select
                  name="contract"
                  value={formData.contract}
                  onChange={handleChange}
                  required
                  className="w-full rounded-lg border-gray-300 shadow-sm focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 p-2.5 border transition-all"
                  disabled={!!editPayment}
                >
                  <option value="">Select Target Contract</option>
                  {contracts.map((c) => (
                    <option key={c._id} value={c._id}>
                      {c.title || c.contractNumber}
                    </option>
                  ))}
                </select>
              </div>

              {/* Payment Type */}
              <div className="space-y-1.5">
                <label className="text-sm font-semibold text-gray-700">
                  Payment Type *
                </label>
                <select
                  name="paymentType"
                  value={formData.paymentType}
                  onChange={handleChange}
                  className="w-full rounded-lg border-gray-300 shadow-sm focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 p-2.5 border"
                >
                  <option value="Milestone">Milestone Payment</option>
                  <option value="Recurring">Recurring / Subscription</option>
                  <option value="One-Time">One-Time Fee</option>
                  <option value="Renewal">Renewal Fee</option>
                </select>
              </div>

              {/* Amount */}
              <div className="space-y-1.5">
                <label className="text-sm font-semibold text-gray-700">
                  Amount *
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <span className="text-gray-500 text-sm">$</span>
                  </div>
                  <input
                    type="number"
                    name="amount"
                    step="0.01"
                    value={formData.amount}
                    onChange={handleChange}
                    required
                    placeholder="0.00"
                    className="w-full rounded-lg border-gray-300 shadow-sm focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 p-2.5 pl-7 border"
                  />
                </div>
              </div>

              {/* Currency */}
              <div className="space-y-1.5">
                <label className="text-sm font-semibold text-gray-700">
                  Currency
                </label>
                <input
                  type="text"
                  name="currency"
                  value={formData.currency}
                  onChange={handleChange}
                  required
                  className="w-full rounded-lg border-gray-300 shadow-sm focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 p-2.5 border bg-gray-50 cursor-not-allowed"
                  readOnly
                />
              </div>

              {/* Due Date */}
              <div className="space-y-1.5">
                <label className="text-sm font-semibold text-gray-700">
                  Due Date *
                </label>
                <input
                  type="date"
                  name="dueDate"
                  value={formData.dueDate}
                  onChange={handleChange}
                  required
                  className="w-full rounded-lg border-gray-300 shadow-sm focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 p-2.5 border"
                />
              </div>

              {/* Status */}
              <div className="space-y-1.5">
                <label className="text-sm font-semibold text-gray-700">
                  Status
                </label>
                <select
                  name="status"
                  value={formData.status}
                  onChange={handleChange}
                  className={`w-full rounded-lg border-gray-300 shadow-sm focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 p-2.5 border font-medium ${
                    formData.status === 'Paid'
                      ? 'text-emerald-600'
                      : formData.status === 'Overdue'
                        ? 'text-red-600'
                        : 'text-amber-600'
                  }`}
                >
                  <option value="Pending">Pending</option>
                  <option value="Paid">Paid</option>
                  <option value="Overdue">Overdue</option>
                  <option value="Cancelled">Cancelled</option>
                </select>
              </div>

              {/* Additional Fields */}
              <div className="space-y-1.5">
                <label className="text-sm font-semibold text-gray-700">
                  Payment Method
                </label>
                <input
                  type="text"
                  name="paymentMethod"
                  placeholder="e.g. Bank Transfer, Credit Card"
                  value={formData.paymentMethod}
                  onChange={handleChange}
                  className="w-full rounded-lg border-gray-300 shadow-sm focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 p-2.5 border"
                />
              </div>
              <div className="space-y-1.5">
                <label className="text-sm font-semibold text-gray-700">
                  Reference / Invoice #
                </label>
                <input
                  type="text"
                  name="reference"
                  placeholder="INV-2024-001"
                  value={formData.reference}
                  onChange={handleChange}
                  className="w-full rounded-lg border-gray-300 shadow-sm focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 p-2.5 border"
                />
              </div>
            </div>

            {/* Notes */}
            <div className="space-y-1.5">
              <label className="text-sm font-semibold text-gray-700">
                Transaction Notes
              </label>
              <textarea
                name="notes"
                rows="3"
                value={formData.notes}
                onChange={handleChange}
                placeholder="Add any internal details or remarks regarding this payment..."
                className="w-full rounded-lg border-gray-300 shadow-sm focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 p-2.5 border resize-none"
              ></textarea>
            </div>
          </form>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-100 flex justify-end items-center gap-3 bg-gray-50/50">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm font-semibold text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-all"
          >
            Cancel
          </button>
          <button
            type="submit"
            form="payment-form"
            disabled={loading}
            className="min-w-[140px] px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-bold rounded-lg shadow-md shadow-blue-200 transition-all flex items-center justify-center gap-2 disabled:bg-blue-300 disabled:shadow-none"
          >
            {loading ? (
              <>
                <Loader2 size={18} className="animate-spin" />
                Processing...
              </>
            ) : (
              <>
                <CheckCircle size={18} />
                {editPayment ? 'Update Payment' : 'Confirm & Save'}
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default PaymentFormModal;
